'use client';

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cn } from '@/lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-5 w-5 rounded-pill',
        'border-2 border-[var(--border-default)]',
        'ring-offset-[var(--bg-primary)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/20 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-[var(--primary)]',
        'transition-all duration-200',
        className
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <div className="h-2.5 w-2.5 rounded-pill bg-[var(--primary)]" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

// Labeled Radio Item
interface RadioGroupLabeledItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: string;
  description?: string;
}

const RadioGroupLabeledItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupLabeledItemProps
>(({ className, label, description, ...props }, ref) => {
  return (
    <label
      className={cn(
        'flex items-start gap-3 cursor-pointer group',
        props.disabled && 'cursor-not-allowed opacity-50'
      )}
    >
      <RadioGroupPrimitive.Item
        ref={ref}
        className={cn(
          'aspect-square h-5 w-5 rounded-pill mt-0.5',
          'border-2 border-[var(--border-default)]',
          'ring-offset-[var(--bg-primary)]',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/20 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          'data-[state=checked]:border-[var(--primary)]',
          'group-hover:border-[var(--primary)]/50',
          'transition-all duration-200',
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
          <div className="h-2.5 w-2.5 rounded-pill bg-[var(--primary)]" />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-[var(--text-primary)]">
          {label}
        </span>
        {description && (
          <span className="text-xs text-[var(--text-tertiary)]">
            {description}
          </span>
        )}
      </div>
    </label>
  );
});
RadioGroupLabeledItem.displayName = 'RadioGroupLabeledItem';

// Card style Radio Item
interface RadioGroupCardItemProps
  extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> {
  label: string;
  description?: string;
  icon?: React.ReactNode;
}

const RadioGroupCardItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupCardItemProps
>(({ className, label, description, icon, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'relative flex items-center gap-3 p-4',
        'rounded-card border-2 border-[var(--border-default)]',
        'bg-[var(--surface-secondary)]',
        'cursor-pointer',
        'ring-offset-[var(--bg-primary)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/20',
        'disabled:cursor-not-allowed disabled:opacity-50',
        'data-[state=checked]:border-[var(--primary)] data-[state=checked]:bg-[var(--primary-subtle)]',
        'hover:border-[var(--primary)]/50',
        'transition-all duration-200',
        className
      )}
      {...props}
    >
      {icon && (
        <div className="flex-shrink-0 text-[var(--text-secondary)] data-[state=checked]:text-[var(--primary)]">
          {icon}
        </div>
      )}
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-medium text-[var(--text-primary)]">
          {label}
        </span>
        {description && (
          <span className="text-xs text-[var(--text-tertiary)] truncate">
            {description}
          </span>
        )}
      </div>
      <div className="flex-shrink-0">
        <div
          className={cn(
            'h-5 w-5 rounded-pill border-2',
            'border-[var(--border-default)]',
            'flex items-center justify-center',
            'transition-all duration-200'
          )}
        >
          <RadioGroupPrimitive.Indicator>
            <div className="h-2.5 w-2.5 rounded-pill bg-[var(--primary)]" />
          </RadioGroupPrimitive.Indicator>
        </div>
      </div>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupCardItem.displayName = 'RadioGroupCardItem';

export { RadioGroup, RadioGroupItem, RadioGroupLabeledItem, RadioGroupCardItem };

